import { createEmptyHistoryState } from "@lexical/react/LexicalHistoryPlugin";
import type {
  Klass,
  LexicalEditor as LexicalEditorType,
  LexicalNode,
  LexicalNodeReplacement,
  SerializedEditorState,
} from "lexical";
import { useCallback, useMemo, type FC } from "react";
import type { ChannelName } from "../../channels/types";
import type { TagGroup } from "../../merge-tag/components/types";
import { LexicalContextProvider } from "./lexical-context";
import { LexicalEditor, type LexicalEditorProps } from "./lexical-editor";
import { LexicalEditorContainer } from "./lexical-editor-container";
import { LexicalParentNodeContextProvider } from "./lexical-parent-node-context";
import "./theme/theme.css";
import type { SerialisedEditorDesign } from "./types/serialised-editor-design";

export type LexicalEditorNodes = ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement>;

export const LexicalEditorWrapper: FC<
  LexicalEditorProps & {
    channelName: ChannelName;
    toChannel?: boolean;
    disabled?: boolean;
    filterTags: (x: string) => TagGroup;
    error?: string;
    design?: SerialisedEditorDesign;
    nodes?: LexicalEditorNodes;
    keyID?: string;
    onError: (e: Error, editor: LexicalEditorType, undoStack: SerializedEditorState[]) => void;
    isSurveyTrigger?: boolean;
    triggerSurveyId?: string | null;
    triggerSurveyName?: string | null;
  }
> = ({
  toChannel,
  error,
  design,
  keyID,
  format,
  disabled,
  filterTags,
  nodes,
  onError,
  channelName,
  isSurveyTrigger,
  triggerSurveyId,
  triggerSurveyName,
  ...rest
}) => {
  const historyState = useMemo(() => createEmptyHistoryState(), []);

  const handleError = useCallback(
    (err: Error, editor: LexicalEditorType) => {
      onError(
        err,
        editor,
        historyState.undoStack.map((entry) => entry.editorState.toJSON()),
      );
    },
    [onError, historyState],
  );

  return (
    <LexicalContextProvider
      format={format}
      disabled={!!disabled}
      filterTags={filterTags}
      toChannel={toChannel}
      channelName={channelName}
      isSurveyTrigger={isSurveyTrigger}
      triggerSurveyId={triggerSurveyId}
      triggerSurveyName={triggerSurveyName}
    >
      <LexicalEditorContainer
        format={format}
        design={design}
        disabled={disabled}
        error={error}
        keyID={keyID || ""}
        nodes={nodes}
        onError={handleError}
      >
        <LexicalParentNodeContextProvider>
          <LexicalEditor format={format} historyState={historyState} {...rest} />
        </LexicalParentNodeContextProvider>
      </LexicalEditorContainer>
    </LexicalContextProvider>
  );
};
